import { until } from '@vueuse/core';
import scrollStore from 'components/SmoothScroll/store';
import { Howl } from 'howler';
import { each, findIndex } from 'lodash';
import { defineStore } from 'pinia';
import AppStore from 'plugins/store/app';
import { pinia } from 'plugins/store/index';
import { preInitRenderer } from 'plugins/webgl/three';
import { preloadImageSequence } from 'plugins/webgl/utils';
import { store as interfaceStore } from 'store/interface';
import { CompressedTexture, Group, NearestFilter, SRGBColorSpace, Texture, TextureLoader } from 'three';
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import { SVGLoader, SVGResult } from "three/examples/jsm/loaders/SVGLoader.js";
import { computed, nextTick, ref } from 'vue';
import assetsToLoad from "./assets";

interface ILoader extends Obj {
  /** is data ready ? */
  loaded: boolean,
  /** is loader hidden (loaded + leave) */
  done: boolean,
  /** because use same cpn fr both - but different aim */
  type: 'loader' | 'transition',
  /**  Before first loader animation */
  runtime: boolean,
  /** Flag if we call the finish spritesheet method */
  spritesheetIsLoading: boolean,
  percent:{total:number, loaded:number},
  assets: {
    textures: {
      [name: string]: Texture;
    },
    spritesheets: {
      [name: string]: CompressedTexture[] | Texture[];
    }
    models: {
      [name: string]: Group;
    },
    svg: {
      [name: string]: SVGResult
    },
    sounds: {
      [name: string]: Howl
    }
  }

  // useTransition: boolean,
  /** is loader in a transition state ? can be usefull - at least in loader cpn */
  // isAnimating: boolean,
  /** use page transition after 1st loader done
   * > start false so use Loader
   * > then true so use PageTransition
   */
}

// for texture
const textureLoader = new TextureLoader();
let ktx2Loader = new KTX2Loader().setTranscoderPath("/libs/basis/");

// for model
const modelLoader = new GLTFLoader();
const dracoLoader = new DRACOLoader();
const svgLoader = new SVGLoader();
dracoLoader.setDecoderPath('/libs/draco/');
dracoLoader.setDecoderConfig({ type: 'js' });
modelLoader.setDRACOLoader(dracoLoader);

const useStore = defineStore('loader', () => {
  const state = ref<ILoader>({
    loaded: false,
    done: false,
    runtime: false,
    type: 'loader',
    spritesheetIsLoading: false,
    percent: {total:0, loaded:0},
    assets: {
      textures: {},
      spritesheets: {},
      models: {},
      svg: {},
      sounds: {},
    },
  });

  //Actions
  const useTransition = () => {
    if (state.value.type === 'transition') return
    state.value.type = 'transition'
  }

  /** loader is loaded
   * if main loader means app/globals/page etc loaded
   * if page transition, means page api loaded
   * 
   * !!! loaded will trigger loader hide
   * you can render stuff now, but the loader is still visible (leave transition)
   * > START RENDER HERE (and maybe init anim paused (hide))
   */
  const setLoaded = (loaded = true) => {
    state.value.loaded = loaded
    if (loaded) {
      nextTick(scrollStore.refreshScroll)
      AppStore.setLoaderReady()
    }
    if (!loaded && state.value.done) {
      setDone(false)
    }
  }

  /** called after leave loader/transition anim
   * It is loaded + leaved
   * > START ANIMS HERE (already render/init etc) */
  const setDone = (done = true) => {
    if (done === state.value.done) return
    state.value.done = done
    if (!done) return
    nextTick(scrollStore.updateScroll)

    useTransition()
  }

  /**
   * Set the first loader animation flag
   */
  const setRuntime = () => {
    state.value.runtime = true;
  }

  // setAnimating(animating=true){
  //   this.state.isAnimating = animating
  // }

  const loadManifest = async () => {
    // check if it's supported or not first
    ktx2Loader = await ktx2Loader.detectSupport(preInitRenderer());

    // @todo load manifest
    const output = Promise.resolve();


    state.value.percent.total = assetsToLoad.length - 8 + (8*20);

    // @todo load manifest
    const promises: Promise<any>[] = [];

    await each(assetsToLoad, async asset => {
    
      // --- Load Single Textures
      if (asset.type === "texture") {
        const pr = new Promise((resolve, reject) => {
          return textureLoader.load(asset.url, (texture => {
            if(!asset.mipmaps) {
              texture.generateMipmaps = false;
              texture.magFilter = NearestFilter;
            }
            state.value.assets.textures[asset.id] = texture;
            state.value.percent.loaded ++;
            return resolve(texture);
          }),
            undefined,
            (e) => reject(e)
          );
        });

        promises.push(pr)
      }

      // --- Load GLTF Model
      if (asset.type === "model") {

        const pr = new Promise((resolve, reject) => {
          return modelLoader.load(asset.url, (gltf => {
            state.value.assets.models[asset.id] = gltf.scene;
            state.value.percent.loaded ++;
            return resolve(gltf);
          }),
            undefined,
            (e) => reject(e)
          );
        });

        promises.push(pr)
      }

      // --- Load SVG Files
      if (asset.type === "svg") {

        const pr = new Promise((resolve, reject) => {
          return svgLoader.load(asset.url, (path => {
            state.value.assets.svg[asset.id] = path;
            state.value.percent.loaded ++;
            return resolve(path);
          }),
            undefined,
            (e) => reject(e)
          );
        });

        promises.push(pr)
      }

      // --- Load Spritesheet Files (only a few, not the whole spriteshet)
      // would be too long to load 8 * 120 images
      if (asset.type === "spritesheet") {
        state.value.assets.spritesheets[asset.id] = [...Array(120)];

        const spritesheetFiles = preloadImageSequence(
          {total:120, startAt:0, requested:20},
          {url:asset.url, files_extension: 'png'},
        );
        
        const loader = spritesheetFiles.loader;
        
        each(spritesheetFiles.urls, (url, index) => {
          const pr = new Promise((resolve, reject) => {
            return loader!.load(url, (texture:any) => {
              state.value.percent.loaded ++;
              texture.colorSpace = SRGBColorSpace
              texture.generateMipmaps = false;
              texture.userData.compressed = texture?.isCompressedTexture;
              state.value.assets.spritesheets[asset.id][index] = texture;
              
              return resolve(texture);
            },
              undefined,
              (e) => reject(e)
            );
          });

          promises.push(pr)
        });
      }

      // --- Load SOunds
      if (asset.type === "sound") {
        const pr = new Promise((resolve) => {
          const sound = new Howl({
            src: [asset.url],
            autoplay: false,
            preload: true,
            volume: .7
          });
          
          state.value.assets.sounds[asset.id] = sound;
          state.value.percent.loaded ++;

          return sound.on('load', () => resolve(sound));
        });

        promises.push(pr)
      }
    });

    return output;
  }

  /**
   * Load the remaining spritesheets
   * @returns 
   */
  const finishSpriteSheetsLoading = () => {

    // do it only once
    if(state.value.spritesheetIsLoading) return;

    const promises: Promise<any>[] = [];
    
    each(assetsToLoad, asset => {
      
      // --- Load Single Textures
      if (asset.type === "spritesheet") {
        
        const array = state.value.assets.spritesheets[asset.id];
        const undefinedIndex = findIndex(array, (t) => t === undefined);

        const spritesheetFiles = preloadImageSequence(
          {total:120, startAt:undefinedIndex, requested:120 - undefinedIndex},
          {url:asset.url, files_extension: 'png'}
        );
        
        const loader = spritesheetFiles.loader;
        each(spritesheetFiles.urls, (url, index) => {
          const pr = new Promise((resolve, reject) => {
            loader!.load(url, (texture:any) => {
              texture.colorSpace = SRGBColorSpace
              texture.generateMipmaps = false;
              texture.userData.compressed = texture?.isCompressedTexture;
              array[undefinedIndex + index] = texture;

              resolve(texture);
            },
              undefined,
              (e) => reject(e)
            );
          });

          promises.push(pr)
        });
      }
    });

    state.value.spritesheetIsLoading = true;
    return Promise.all(promises)
  }

  const waitAppReady = () => {
    return until(AppStore.ready()).toBe(true)
  }

  const getAsset = (id: string, type: string) => {
    switch (type) {
      case "svg":
        return state.value.assets.svg[id] as SVGResult
      case "spritesheet":
        return state.value.assets.spritesheets[id] as CompressedTexture[] | Texture[];
      case "texture":
        return state.value.assets.textures[id] as Texture;
      case "model":
        return state.value.assets.models[id] as Group;
      default:
        return state.value.assets.textures[id] as Texture;
    }
  }

  const getTexture = (id: string): Texture => {
    return state.value.assets.textures[id] as Texture;
  }
  const getModel = (id: string): Group => {
    return state.value.assets.models[id] as Group;
  }
  const getSvg = (id: string): SVGResult => {
    return state.value.assets.svg[id] as SVGResult;
  }
  const getSpritesheet = (id: string): CompressedTexture[] | Texture[] => {
    return state.value.assets.spritesheets[id] as CompressedTexture[] | Texture[] ;
  }
  const getSound = (id: string): Howl => {
    return state.value.assets.sounds[id] as Howl ;
  }

  const getLoader = (type:string): O<TextureLoader | KTX2Loader> => {
    switch(type) {
      default:
      case "texture":
      return textureLoader;

      case "spritesheet":
      return ktx2Loader.workerConfig.etc1Supported && interfaceStore.highResVersion ? ktx2Loader : undefined;
    }
  }

  return {
    state,

    //getters
    loaded: state.value.loaded,
    done: state.value.done,
    type: state.value.type,
    runtime: state.value.runtime,
    isSpritesheetLoading: state.value.spritesheetIsLoading,
    percentProgress: computed( () => state.value.percent),
    allPreloaded: computed( () => state.value.percent.loaded === state.value.percent.total),

    //actions
    useTransition,
    setLoaded,
    setDone,
    loadManifest,
    waitAppReady,
    setRuntime,
    getAsset,
    getTexture,
    getModel,
    getSvg,
    getLoader,
    getSound,
    getSpritesheet,
    finishSpriteSheetsLoading,
  }
});

export const store = useStore(pinia);
export default store;
